// frontend/src/api/types.ts - COMPREHENSIVE ENHANCED TYPE SAFETY VERSION 6.0.0
// Complete type definitions with latest route integration and middleware support

// ===== CORE DOMAIN TYPES =====

export interface User {
  _id: string;
  id?: string;
  username: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  role: 'user' | 'admin' | 'contributor' | 'implementor';
  organization: string | Organization;
  organizationId?: string;
  organizationName?: string;
  department?: string;
  groups: string[] | Group[];
  emailAccounts: EmailAccount[];
  isActive?: boolean;
  deactivated?: boolean; // Make this optional
  lastLogin?: string;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt?: string;
  deletedAt?: string;
  deletedBy?: string;
  loginAttempts?: number;
  lockUntil?: Date | string;
}

export interface NormalizedUser {
  _id: string;
  id: string;
  username: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  role: 'user' | 'admin' | 'contributor' | 'implementor';
  organization: {
    _id: string;
    name: string;
  };
  groups: Array<{
    _id: string;
    name: string;
    type: 'visibility' | 'approval' | 'system';
  }>;
  department?: string;
  emailAccounts: EmailAccount[];
  createdAt: string;
  updatedAt: string;
  lastLoginAt?: string;
  deactivated: boolean;
  deletedAt?: string;
  loginAttempts?: number;
  lockUntil?: Date | string;
  isActive: boolean;
  emailAccountCount: number;
  groupCount: number;
}

export interface Organization {
  // Core identification
  id: string;
  _id?: string; // MongoDB ObjectId for compatibility
  name: string;
  description?: string;
  
  // Resource limits
  maxUsers?: number | null;
  maxProjects?: number | null;
  maxGroups?: number | null;
  maxWorkflows?: number | null;
  
  // Email management limits
  maxEmailAccounts?: number | null;
  emailRetentionDays?: number;
  maxEmailStorage?: number | null;
  maxCorrespondenceStorage?: number | null;
  maxTotalStorage?: number | null;
  emergencyPreservationEnabled?: boolean;
  
  // Legacy storage field
  maxStorage?: number | null;
  maxCorrespondences?: number | null;
  
  // Subscription and features
  plan?: 'basic' | 'standard' | 'enterprise';
  isAIEnabled?: boolean;
  subscribedModules?: string[];
  
  // Status
  isActive?: boolean;
  deactivated?: boolean;
  
  // BACKEND FIELD NAMES (from Organization.js model)
  numUsers?: number;           // Backend uses numUsers, not userCount
  numProjects?: number;        // Backend uses numProjects, not projectCount  
  numGroups?: number;
  numWorkflows?: number;
  numEmailAccounts?: number;
  numEmails?: number;          // Backend uses numEmails, not emailCount
  numCorrespondences?: number; // Backend uses numCorrespondences, not correspondenceCount
  
  // FRONTEND ALIASES (for backward compatibility and API transformation)
  userCount?: number;          // Alias for numUsers
  projectCount?: number;       // Alias for numProjects
  groupCount?: number;         // Alias for numGroups
  workflowCount?: number;      // Alias for numWorkflows
  emailCount?: number;         // Alias for numEmails
  correspondenceCount?: number; // Alias for numCorrespondences

  // Calculated fields (added by backend transformation)
  totalStorageUsed?: number;   // Sum of totalEmailSize + totalCorrespondenceSize
  totalEmailSize?: number;     // Email storage in bytes
  totalCorrespondenceSize?: number; // Correspondence storage in bytes
  
  // Legacy usage fields
  totalStorageUsage?: number;
  
  // Enhanced metrics structures
  storageMetrics?: {
    totalEmailStorage: number;
    totalCorrespondenceStorage: number;
    totalStorageUsage: number;
    totalStaticDocumentSize?: number;
    totalAttachmentSize?: number;
    emailAttachmentStorage?: number;
    emailAttachmentCount?: number;
    correspondenceAttachmentStorage?: number;
    correspondenceAttachmentCount?: number;
  };
  
  emailMetrics?: {
    totalEmails: number;
    preservedEmails: number;
    temporaryEmails: number;
    expiredEmails: number;
    emailsPendingDeletion?: number;
    emailPreservationRate: number;
    averageEmailRetentionDays: number;
    lastEmailCleanup?: string;
  };
  
  correspondenceMetrics?: {
    totalCorrespondences: number;
    projectCorrespondences?: number;
    personalCorrespondences?: number;
    manualCorrespondences: number;
    autoClassifiedCorrespondences: number;
    correspondencesWithAttachments?: number;
    manuallyReviewedCorrespondences?: number;
    emailToCorrespondenceConversionRate: number;
    averageClassificationScore?: number;
  };
  
  // Policies
  correspondencePolicy?: {
    autoClassificationEnabled: boolean;
    requireManualReview: boolean;
    preservationPriority: 'high' | 'medium' | 'low';
    maxAttachmentSize: number;
  };
  
  // Email retention policy
  emailRetentionPolicy?: {
    defaultRetentionDays: number;
    applyToExistingEmails: boolean;
    autoPreservationEnabled: boolean;
    emergencyPreservationEnabled: boolean;
    createdAt: string;
    lastUpdatedAt?: string;
    lastUpdatedBy?: string;
  };
  
  // Timestamps
  createdAt: string;
  updatedAt?: string;
  deletedAt?: string | null;
  deletedBy?: string;
  
  // Virtual/computed fields (may be present in responses)
  emailStorageUsagePercentage?: number;
  correspondenceStorageUsagePercentage?: number;
  totalStorageUsagePercentage?: number;
  userUsagePercentage?: number;
  emailPreservationRatePercentage?: number;
  correspondenceConversionRatePercentage?: number;
  retentionPolicyStatus?: 'configured' | 'default';
  
  // Relationships (populated in some responses)
  users?: User[];
  groups?: Group[];
  projects?: Project[];
  workflows?: Workflow[];
  
  // Additional fields that may be returned
  metricsCache?: {
    lastCalculated: string;
    data: any;
  };
}

// ===== EMAIL LIFECYCLE MANAGEMENT TYPES =====

export interface EmailRetentionPolicy {
  defaultRetentionDays: number;
  applyToExistingEmails: boolean;
  autoPreservationEnabled: boolean;
  emergencyPreservationEnabled: boolean;
  createdAt: string;
  lastUpdatedAt?: string;
  lastUpdatedBy?: string;
}

export interface CorrespondencePolicy {
  autoCreateCorrespondence: boolean;
  requireManualReview: boolean;
  defaultVisibilityGroups: string[];
  preserveEmailAttachments: boolean;
  createdAt: string;
  lastUpdatedAt?: string;
}

export interface StorageMetrics {
  totalStorage: number;
  usedStorage: number;
  availableStorage: number;
  usagePercentage: number;
  lastUpdated: string;
}

export interface EmailMetrics {
  totalEmails: number;
  temporaryEmails: number;
  preservedEmails: number;
  expiredEmails: number;
  storageUsed: number;
  averageEmailSize: number;
  preservationRate: number;
  lastUpdated: string;
}

export interface CorrespondenceMetrics {
  totalCorrespondences: number;
  withAttachments: number;
  storageUsed: number;
  conversionRate: number;
  averageProcessingTime: number;
  lastUpdated: string;
}

// ===== GROUP TYPES =====

export interface Group {
  _id?: string;
  id?: string;
  name: string;
  type: 'visibility' | 'approval' | 'system';
  description?: string;
  organization: string | Organization;
  members: string[] | User[];
  memberCount?: number;
  activeMemberCount?: number;
  department?: string;
  isSystemGroup?: boolean;
  isActive?: boolean;
  permissions?: {
    canView?: boolean;
    canEdit?: boolean;
    canApprove?: boolean;
    canDelete?: boolean;
    canViewMembers?: boolean;
    canJoinSelf?: boolean;
    canLeaveSelf?: boolean;
    requireApproval?: boolean;
  };
  createdAt: string;
  updatedAt?: string;
  createdBy?: string;
  lastModifiedBy?: string;
  deletedAt?: string;
  deletedBy?: string;
}

// ===== EMAIL TYPES =====

export interface EmailAccount {
  _id?: string;
  id?: string;
  email: string;
  name?: string; // Custom name for the account
  provider: 'gmail' | 'outlook' | 'imap' | 'exchange';
  isActive: boolean;
  isPrimary?: boolean;
  userId?: string;
  organization?: string;
  lastSyncAt?: string;
  lastSuccessfulSyncAt?: string; // Last successful sync timestamp
  syncStatus: 'connected' | 'error' | 'pending' | 'success' | 'unknown';
  credentialsValid?: boolean;
  errorMessage?: string;
  lastErrorAt?: string;
  totalEmails: number;
  unreadEmails: number;
  needsReauthentication: boolean;
  hasAccessToken: boolean;
  hasRefreshToken: boolean;
  syncCount?: number;
  addedAt?: string;
  settings?: any;
  createdAt: string;
  updatedAt?: string;
}

export interface EmailCorrespondenceLink {
  correspondenceId: string | Correspondence; // Can be populated
  projectId?: string;
  projectName?: string;
  isPersonal: boolean;
  classificationScore?: number;
  preserveAttachments?: boolean;
  createdAt: string;
}

export interface Email {
  _id?: string;
  id?: string;
  messageId: string;
  subject: string;
  body: string;
  from: EmailAddress;
  to: EmailAddress[];
  cc?: EmailAddress[];
  bcc?: EmailAddress[];
  date: string;

  // Email metadata
  provider: EmailProvider;
  folder: EmailFolder;
  isImportant: boolean;
  hasAttachments: boolean;
  size: number;

  // Lifecycle management
  preservationStatus: EmailRetentionStatus;
  autoDeleteAt?: string;
  preservedAt?: string;
  preservedBy?: string;
  preservationReason?: string;
  processingStatus: EmailProcessingStatus;
  retentionDays?: number;
  daysUntilDeletion?: number;

  // Classification
  projectId?: string;
  hasCorrespondences?: boolean;
  classificationScore?: ClassificationScore;

  // Correspondence relationships (can be populated with full Correspondence objects)
  correspondences?: EmailCorrespondenceLink[] | Correspondence[];

  // Organization and user
  userId: string;
  organization: string;

  // Attachments
  attachments: string[] | Attachment[];

  // Timestamps
  createdAt: string;
  updatedAt?: string;
  archivedAt?: string;
}

// ===== EMAIL ENUM TYPES =====

export type EmailProvider = 'gmail' | 'outlook' | 'imap' | 'exchange';
export type EmailFolder = 'inbox' | 'sent' | 'drafts' | 'trash' | 'archive' | 'custom';
export type EmailRetentionStatus = 'temporary' | 'preserved' | 'emergency_preserved' | 'pending_deletion';
export type EmailProcessingStatus = 'pending' | 'processing' | 'completed' | 'error';

export interface EmailAddress {
  name?: string;
  email: string;
}

export interface ClassificationScore {
  overall: number;
  confidence: number;
  source: 'subject' | 'body' | 'sender' | 'attachment';
}

// ===== PROJECT TYPES =====

export interface Project {
  _id?: string;
  id?: string;
  name: string;
  description?: string;
  organization: string | Organization;
  
  // Project configuration
  isActive: boolean;
  isPublic?: boolean;
  priority?: Priority;
  
  // Access control
  visibilityGroups: string[] | Group[];
  createdBy: string;
  
  // Usage statistics
  correspondenceCount?: number;
  responseCount?: number;
  
  // Timestamps
  createdAt: string;
  updatedAt?: string;
  deletedAt?: string;
  deletedBy?: string;
}

// ===== CORRESPONDENCE TYPES =====

export interface Correspondence {
  _id?: string;
  id?: string;
  subject: string;
  body?: string; // V3: Made optional since content is now in a separate field
  content?: string; // V3: New field for main content

  // Source tracking
  sourceEmailIds?: string[]; // V3: Made optional
  emailId?: string; // V3: Direct email reference
  sourceType?: 'email' | 'manual' | 'imported';

  // Classification
  projectId?: string;
  manual?: boolean; // V3: Made optional
  classificationScore?: ClassificationScore;
  classificationMetadata?: ClassificationMetadata;

  // V3: Project relationship (new structure)
  projectRelationship?: {
    projectId: string;
    projectName?: string;
    classificationMethod?: 'manual' | 'auto-detection';
    classificationScore?: number;
    matchedKeywords?: any[];
    matchedConditions?: any[];
    classifiedBy?: string;
    classifiedAt?: string;
  };

  // Content
  extractedContent?: string;
  hasAttachments?: boolean; // V3: Made optional
  hasResponse?: boolean;
  responseGenerated?: boolean;
  responseId?: string | Response;

  // V3: New flags for split model structure
  hasEmailContent?: boolean;
  hasAISummary?: boolean;
  attachmentCount?: number;

  // V3: Email preservation
  emailPreserved?: boolean;
  preservationReason?: string;
  emailAutoDeleteAt?: string;

  // V3: Document metadata
  documentType?: 'email' | 'letter' | 'memo' | 'report' | 'contract' | 'invoice' | 'form' | 'other';
  direction?: 'inward' | 'outward' | 'internal';
  documentDate?: string;
  documentReference?: string;
  /** Set while the source document is still being OCR'd; letter-derived
   *  fields (reference, date, subject) are empty until 'done'. */
  sourceExtraction?: { status?: 'pending' | 'done' | 'failed'; attachmentId?: string; queuedAt?: string; completedAt?: string };
  tags?: string[];
  customFields?: Array<{
    name: string;
    value: string;
    type?: string;
  }>;
  notes?: string;

  // Organization and user
  userId?: string; // V3: Made optional
  organization?: string; // V3: Made optional
  visibilityGroups?: string[] | Group[];

  // V3: Email details (from field)
  from?: EmailAddress;
  to?: EmailAddress[];
  cc?: EmailAddress[];

  // Attachments and responses (legacy)
  attachments?: string[] | Attachment[];
  responses?: string[] | Response[];

  // Timestamps
  createdAt: string;
  updatedAt?: string;
  archivedAt?: string;
  deletedAt?: string;
  createdBy?: string;
  lastAccessedAt?: string;
  lastAccessedBy?: string;
  accessCount?: number;
}

export interface ClassificationMetadata {
  algorithm?: string;
  processingTime?: number;
  confidence?: 'low' | 'medium' | 'high';
  reviewRequired?: boolean;
  reviewedBy?: string;
  reviewedAt?: string;
}

// ===== V3: CORRESPONDENCE SPLIT MODEL TYPES =====

export interface CorrespondenceEmail {
  _id: string;
  correspondenceId: string;
  organization: string;

  // Email headers
  messageId: string;
  threadId?: string;
  subject: string;
  from: EmailAddress;
  to: EmailAddress[];
  cc?: EmailAddress[];
  bcc?: EmailAddress[];
  replyTo?: EmailAddress;

  // Email content
  body: {
    text?: string;
    html?: string;
  };
  date: string;
  receivedDate?: string;

  // Email metadata
  folder?: string;
  labels?: string[];
  size?: number;
  isRead?: boolean;
  isStarred?: boolean;
  isImportant?: boolean;
  priority?: 'low' | 'normal' | 'high';
  hasAttachments?: boolean;
  deliveryStatus?: string;
  spamScore?: number;

  // Headers
  headers?: Record<string, string>;

  // Preservation metadata
  originalEmailId: string;
  preservedBy: string;
  preservedAt?: string;

  // Timestamps
  createdAt: string;
  updatedAt?: string;
}

export interface CorrespondenceAttachment {
  _id: string;
  correspondenceId: string;
  organization: string;

  // File information
  filename: string;
  originalName?: string;
  contentType: string;
  size: number;

  // S3 storage
  s3Key: string;
  s3Url?: string;
  s3Bucket?: string;

  // File integrity
  fileHash?: string;

  // Processing status
  processingStatus: 'pending' | 'processing' | 'completed' | 'error';
  virusScanStatus: 'pending' | 'clean' | 'infected' | 'error';

  // Content extraction
  extractedContent?: string;
  extractionMethod?: 'text' | 'ocr' | 'hybrid';

  // OCR data
  ocrData?: {
    documentType?: string;
    extractedText?: string;
    extractedSubject?: string;
    confidence?: number;
    language?: string;
    pageCount?: number;
    pages?: Array<{
      pageNumber: number;
      text: string;
      confidence: number;
    }>;
    metadata?: Record<string, any>;
  };

  // Source tracking
  sourceType: 'email' | 'upload' | 'generated';
  sourceEmailId?: string;
  sourceAttachmentId?: string;

  // Access tracking
  downloadCount?: number;
  lastAccessedAt?: string;
  lastAccessedBy?: string;

  // User tracking
  createdBy: string;
  createdAt: string;
  updatedAt?: string;
}

export interface CorrespondenceAI {
  _id: string;
  correspondenceId: string;
  organization: string;

  // Content summary
  summary?: string;
  keyTopics?: string[];
  category?: string;

  // Entity extraction
  extractedEntities?: Array<{
    type: 'person' | 'organization' | 'location' | 'date' | 'money' | 'other';
    text: string;
    confidence: number;
    startIndex?: number;
    endIndex?: number;
  }>;

  // Sentiment analysis
  sentiment?: {
    overall: 'positive' | 'neutral' | 'negative';
    score: number;
    confidence: number;
  };

  // Intent detection
  intent?: {
    primary: string;
    confidence: number;
    secondary?: string[];
  };

  // Classification suggestions
  suggestedProjects?: Array<{
    projectId: string;
    projectName: string;
    confidence: number;
    reasons: string[];
  }>;

  // Processing metadata
  model?: string;
  modelVersion?: string;
  processingTime?: number;
  confidenceScore?: number;

  // Processing status
  status: 'pending' | 'processing' | 'completed' | 'error';
  errorMessage?: string;

  // Timestamps
  processedAt?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface CorrespondenceAudit {
  _id: string;
  correspondenceId: string;
  organization: string;

  // Action details
  action: 'create' | 'update' | 'delete' | 'restore' | 'assign_project' | 'add_attachment' | 'create_response' | 'other';
  performedBy: string;
  timestamp: string;

  // Version tracking
  version?: number;
  changeType?: 'content' | 'metadata' | 'relationship' | 'status';

  // Changes made
  changes?: Array<{
    field: string;
    oldValue?: any;
    newValue?: any;
  }>;

  // Additional context
  notes?: string;
  reason?: string;
  ipAddress?: string;
  userAgent?: string;

  // Compliance metadata
  complianceFlags?: string[];
  retentionRequirement?: string;

  // Timestamps
  createdAt: string;
}

// V3: Response types for V3 endpoints
export interface CorrespondenceWithRelated extends Omit<Correspondence, 'attachments'> {
  emailContent?: CorrespondenceEmail;
  attachments?: CorrespondenceAttachment[]; // V3: Full attachment objects
  aiSummary?: CorrespondenceAI;
  auditTrail?: CorrespondenceAudit[];
}

export interface CorrespondenceFull {
  correspondence: Correspondence;
  emailContent?: CorrespondenceEmail;
  attachments?: CorrespondenceAttachment[];
  aiSummary?: CorrespondenceAI;
  auditTrail?: CorrespondenceAudit[];
}

// V3: Query parameters for lazy loading
export interface CorrespondenceV3Options {
  includeEmail?: boolean;
  includeAttachments?: boolean;
  includeAI?: boolean;
  includeAudit?: boolean;
}

// ===== WORKFLOW TYPES =====

export interface Workflow {
  _id?: string;
  id?: string;
  name: string;
  description?: string;
  organization: string;
  
  // Core workflow definition
  stages: WorkflowStage[];
  version?: number;
  
  // Template configuration
  isTemplate?: boolean;
  templateCategory?: 'standard' | 'expedited' | 'review-heavy' | 'simple' | 'custom';
  
  // Workflow metadata
  priority?: Priority;
  isActive: boolean;
  isClosed?: boolean;
  
  // Performance and constraints
  stats?: WorkflowStats;
  constraints?: WorkflowConstraints;
  
  // Audit fields
  createdBy: string;
  lastModifiedBy?: string;
  
  // Soft delete
  deletedAt?: string;
  deletedBy?: string;
  
  // Timestamps
  createdAt: string;
  updatedAt: string;
  
  // Additional computed fields from backend
  stageCount?: number;
  hasValidStages?: boolean;
  canEdit?: boolean;
  stageSummary?: Array<{
    name: string;
    role: string;
    index: number;
  }>;
  usageCount?: number;
  lastUsed?: string;
}

export interface WorkflowStage {
  _id?: string;
  stageName: string; // Backend uses stageName, not name
  stageIndex: number;
  role: StageRole;
  mandatory?: boolean;
  
  // Group assignments
  approvalGroups?: string[];
  
  // Stage configuration
  timeLimit?: { hours: number };
  allowParallel?: boolean;
  autoAdvance?: boolean;
  requiredApprovals?: number;
  
  // Performance tracking
  stats?: StageStats;
  
  // Timestamps
  createdAt?: string;
  updatedAt?: string;
}

export interface WorkflowConstraints {
  maxParallelResponses?: number;
  globalTimeLimit?: number; // in hours
  allowedDepartments?: string[];
  minimumUserRole?: 'contributor' | 'admin' | 'implementor';
}

export interface WorkflowStats {
  responsesProcessed: number;
  avgCompletionTime: number;
  successRate: number;
  timeoutRate: number;
  lastUsedAt?: string;
}

export interface StageStats {
  responsesProcessed: number;
  avgProcessingTime: number;
  timeoutCount: number;
}

// ===== RESPONSE TYPES =====

export interface ResponseVersion {
  _id?: string;
  content: string;
  attachments: string[];
  versionNumber: number;
  createdBy: string;
  createdAt: string;
  comment?: string;
}

export interface ResponseActionHistory {
  _id?: string;
  action: 'Created' | 'Edited' | 'Commented' | 'Approved' | 'Rejected' | 'Sent' | 'DraftUploaded';
  userId: string;
  stageName: string;
  comment?: string;
  timestamp: string;
  ipAddress?: string;
  userAgent?: string;
}

export interface ResponsePendingAction {
  userId: string;
  stageName: string;
  actionType: 'edit' | 'comment' | 'approve' | 'send';
  dueDate?: string;
  notified?: boolean;
}

export interface ResponseDraftLockReleaseRequest {
  userId: string | { _id: string; username?: string; firstName?: string; lastName?: string; email?: string };
  requestedAt: string;
  reason?: string;
  status: 'pending' | 'approved' | 'denied';
  resolvedAt?: string;
  resolvedBy?: string;
}

export interface ResponseDraftLock {
  isLocked: boolean;
  lockedBy?: string | { _id: string; username?: string; firstName?: string; lastName?: string; email?: string };
  lockedAt?: string;
  lockExpiry?: string;
  releaseRequests?: ResponseDraftLockReleaseRequest[];
}

export interface Response {
  _id?: string;
  id?: string;

  // Source references
  sourceIds: string[];
  projectIds: string[];
  visibilityGroups: string[];

  // Workflow
  workflowId: string;
  currentStage: string;
  workflowStageIndex: number;
  stageComplete: boolean;
  status: ResponseStatus;

  // Organization and user
  organization: string;
  userId: string;

  // Content
  responsePoints?: string[];
  mailSubject: string;
  mailBody: string;
  attachments: string[];

  // Version management
  versions: ResponseVersion[];
  currentVersion: number;

  // Action tracking
  actionHistory: ResponseActionHistory[];
  pendingActions?: ResponsePendingAction[];

  // Draft locking
  draftLock?: ResponseDraftLock;

  // Flags and metadata
  closedByCreator?: boolean;
  aiGenerated?: boolean;

  // Permissions (computed by backend)
  canEdit?: boolean;
  canApprove?: boolean;
  canDelete?: boolean;
  canComment?: boolean;
  canReject?: boolean;
  canSend?: boolean;
  canViewHistory?: boolean;

  // Timestamps
  createdAt: string;
  updatedAt: string;
  sentAt?: string;
}

export interface ResponseComment {
  id: string;
  responseId: string;
  userId: string;
  content: string;
  type: 'comment' | 'suggestion' | 'approval';
  isResolved: boolean;
  resolvedBy?: string;
  resolvedAt?: string;
  createdAt: string;
  updatedAt?: string;
}

// ===== ATTACHMENT TYPES =====

export interface Attachment {
  _id?: string;
  id?: string;
  filename: string;
  originalName: string;
  contentType: string;
  size: number;
  s3Key: string;
  s3Url?: string;
  
  // Content processing
  extractedContent?: string;
  ocrText?: string;
  conversionSuccessful?: boolean;
  ocrApplied?: boolean;
  
  // Security and processing
  virusScanStatus: VirusScanStatus;
  virusScanResult?: any;
  processingStatus: ProcessingStatus;
  
  // References
  emailId?: string;
  correspondenceId?: string;
  responseId?: string;
  staticDocumentId?: string;
  projectId?: string;
  attachmentType?: AttachmentType;
  
  // Storage management
  storageTier?: StorageTier;
  sourceEmailId?: string;
  fileHash?: string;
  
  // Access control
  userId: string;
  organization: string;
  visibilityGroups?: string[];
  isPublic: boolean;
  
  // Usage tracking
  downloadCount?: number;
  lastAccessedAt?: string;
  uploadedBy?: string;
  
  // Metadata
  tags?: string[];
  description?: string;
  
  // Timestamps
  createdAt: string;
  updatedAt?: string;
  deletedAt?: string;
  deletedBy?: string;
}

// ===== NOTIFICATION TYPES =====

export interface Notification {
  _id?: string;
  id?: string;
  type: NotificationType;
  title: string;
  message: string;
  userId: string;
  organization: string;
  
  // Notification metadata
  category: NotificationCategory;
  priority: Priority;
  isRead: boolean;
  readAt?: string;
  
  // Action link
  actionUrl?: string;
  actionText?: string;
  
  // Related entities
  relatedEntityId?: string;
  relatedEntityType?: 'correspondence' | 'response' | 'project' | 'user' | 'organization';
  
  // Timestamps
  createdAt: string;
  updatedAt?: string;
  expiresAt?: string;
}

// ===== ENUM TYPES =====

export type UserRole = 'user' | 'admin' | 'contributor' | 'implementor';
export type Priority = 'low' | 'medium' | 'normal' | 'high' | 'urgent' | 'critical';
export type ResponseStatus = 'draft' | 'in_review' | 'approved' | 'sent' | 'scheduled' | 'cancelled';
export type StageRole = 'Editor' | 'Commenter' | 'Approver' | 'Sender';
export type AttachmentType = 'email' | 'correspondence' | 'response' | 'static';
export type StorageTier = 'standard' | 'archive' | 'temp';
export type ProcessingStatus = 'pending' | 'processing' | 'completed' | 'error';
export type VirusScanStatus = 'pending' | 'clean' | 'infected' | 'error';
export type NotificationType =
  | 'NEW_USER_PASSWORD'
  | 'EMAIL_CLASSIFIED'
  | 'RESPONSE_CREATED'
  | 'RESPONSE_UPDATED'
  | 'RESPONSE_APPROVED'
  | 'RESPONSE_REJECTED'
  | 'RESPONSE_SENT'
  | 'RESPONSE_STAGE_ADVANCED'
  | 'WORKFLOW_COMPLETED'
  | 'UNAUTHORIZED_SEND'
  | 'PROJECT_ASSIGNED'
  | 'GROUP_MEMBERSHIP_CHANGED'
  | 'SYSTEM_NOTIFICATION'
  | 'DEADLINE_REMINDER'
  | 'OVERDUE_ALERT'
  | 'BATCH_NOTIFICATION'
  // Legacy generic types for backwards compatibility
  | 'info' | 'warning' | 'error' | 'success';
export type NotificationCategory = 'system' | 'workflow' | 'correspondence' | 'project' | 'email' | 'storage' | 'security';

// ===== UTILITY INTERFACES =====

export interface UploadedFile {
  id: string;
  filename: string;
  originalName: string;
  size: number;
  mimeType: string;
  url?: string;
  isPublic: boolean;
  extractedContent?: string;
  processingStatus?: ProcessingStatus;
  virusScanStatus?: VirusScanStatus;
}

// ===== QUERY PARAMETER INTERFACES =====

export interface QueryParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface UsersQueryParams extends QueryParams {
  role?: UserRole;
  status?: 'active' | 'deactivated' | 'all';
  department?: string;
  organizationId?: string;
}

export interface OrganizationUsersQueryParams extends QueryParams {
  role?: UserRole;
  status?: 'active' | 'deactivated' | 'all';
  department?: string;
}

export interface OrganizationQueryParams extends QueryParams {
  status?: 'active' | 'deactivated' | 'all';
  emailLifecycleCompliance?: 'all' | 'configured' | 'default';
  storageType?: 'all' | 'email' | 'correspondence' | 'total';
}

export interface GroupsQueryParams extends QueryParams {
  type?: 'visibility' | 'approval' | 'all';
  organizationId?: string; // For implementor cross-org access
  isActive?: boolean; // Filter by active/inactive status
  includeAssignments?: boolean; // Include project/workflow assignments
}

export interface CorrespondenceQueryParams extends QueryParams {
  q?: string;
  projectId?: string;
  userId?: string;
  hasProject?: boolean;
  manual?: boolean;
  hasAttachments?: boolean;
  hasResponse?: boolean;
  dateFrom?: string;
  dateTo?: string;
  minScore?: number;
  maxScore?: number;
  direction?: 'inward' | 'outward' | 'internal';
  correspondenceStatus?: 'open' | 'partially_closed' | 'closed';
  issueTags?: string;
  [key: string]: any; // Dynamic custom field filters (cf_fieldId)
}

export interface EmailQueryParams extends QueryParams {
  userId?: string;
  section?: EmailFolder;
  provider?: EmailProvider;
  sortBy?: 'date' | 'from' | 'subject' | 'size' | 'preservationStatus' | 'autoDeleteAt';
  sortOrder?: 'asc' | 'desc';
  search?: string;
  startDate?: string;
  endDate?: string;
  hasAttachments?: boolean;
  isImportant?: boolean;
  projectId?: string;
  preservationStatus?: EmailRetentionStatus;
  hasCorrespondences?: boolean;
  processingStatus?: EmailProcessingStatus;
  retentionDays?: number;
  preservedBy?: string;
  daysUntilDeletion?: number;
}

export interface AttachmentQueryParams extends QueryParams {
  emailId?: string;
  correspondenceId?: string;
  responseId?: string;
  attachmentType?: AttachmentType;
  storageTier?: StorageTier;
  contentType?: string;
  search?: string;
  sortBy?: 'createdAt' | 'filename' | 'size' | 'downloadCount' | 'lastAccessedAt';
  sortOrder?: 'asc' | 'desc';
  isPublic?: boolean;
  processingStatus?: ProcessingStatus;
  virusScanStatus?: VirusScanStatus;
}

export interface ResponseQueryParams extends QueryParams {
  status?: ResponseStatus;
  priority?: Priority;
  workflow?: string;
  project?: string;
  stage?: string;
  search?: string;
  sortBy?: 'createdAt' | 'updatedAt' | 'priority' | 'status' | 'referenceNumber';
  sortOrder?: 'asc' | 'desc';
  dateFrom?: string;
  dateTo?: string;
}

export interface WorkflowQueryParams extends QueryParams {
  isTemplate?: boolean;
  priority?: Priority;
  isActive?: boolean;
  organizationId?: string; // For implementor cross-org access
}

export interface ProjectQueryParams extends QueryParams {
  status?: 'active' | 'archived' | 'all';
  isPublic?: boolean;
  priority?: Priority;
  hasCorrespondences?: boolean;
}

export interface NotificationQueryParams extends QueryParams {
  type?: NotificationType;
  category?: NotificationCategory;
  isRead?: boolean;
  priority?: Priority;
  dateFrom?: string;
  dateTo?: string;
}

// ===== STANDARDIZED RESPONSE TYPES =====

export interface BaseApiResponse {
  success?: boolean;
  message?: string;
  error?: string;
  timestamp?: string;
  requestId?: string;
}

export interface StandardResponse<T> extends BaseApiResponse {
  data?: T;
}

export interface PaginatedResponse<T> extends BaseApiResponse {
  data: T[];
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    hasNextPage?: boolean;
    hasPrevPage?: boolean;
  };
  total: number;
  totalPages: number;
  page: number;
  limit: number;
  analytics?: any;
  organizationAnalytics?: any[];
  summary?: any;
  filters?: any;
  metadata?: any;
}

export interface UserListResponse extends PaginatedResponse<User> {}

export interface OrganizationListResponse extends PaginatedResponse<Organization> {
  // Enhanced analytics for list view
  analytics: {
    // Total counts across all organizations
    totalUsers: number;
    totalEmailStorage: number;
    totalCorrespondenceStorage: number;
    totalStorage: number;
    totalProjects: number;
    totalEmails: number;
    totalPreservedEmails: number;
    totalCorrespondences: number;
    
    // Organization status counts
    activeOrgs: number;
    deactivatedOrgs: number;
    orgsWithRetentionPolicy: number;
    
    // Average metrics
    avgEmailPreservationRate: number;
    avgConversionRate: number;
    emailPreservationRatePercentage: number;
    correspondenceConversionRatePercentage: number;
    retentionPolicyAdoption: number;
  };
  
  // Applied filters (for debugging/UI state)
  filters: {
    search?: string;
    status?: string;
    sortBy?: string;
    sortOrder?: string;
    emailLifecycleCompliance?: string;
    storageType?: string;
  };
  
  requestId?: string;
}

export interface StandardResponse<T> extends BaseApiResponse {
  data?: T;
  user?: T; // For auth responses
  success?: boolean;
}

export interface ApiErrorResponse extends BaseApiResponse {
  success: false;
  error: string;
  status: number;
  code?: string;
  errors?: Array<{
    field?: string;
    message: string;
    value?: any;
  }>;
  /** Field-level detail behind a 400; set by the backend error middleware. */
  validationErrors?: Array<{
    field?: string;
    message: string;
    value?: any;
  }>;
  details?: any;
  expiredAt?: string;
  activeAt?: string;
}


// ===== ENHANCED AUTHENTICATION TYPES =====

export interface LoginCredentials {
  username: string;
  password: string;
  organization: string;
}

export interface AuthResponse {
  success?: boolean;
  user: {
    _id: string;
    username: string;
    role: 'user' | 'admin' | 'contributor' | 'implementor';
    organization: string; // Backend returns org ID for login
    organizationId: string;
    organizationName: string;
    groups: Array<{
      _id: string;
      name: string;
      type: 'visibility' | 'approval' | 'system';
    }>;
    department?: string;
  };
  accessToken: string;
  refreshToken: string;
  token?: string; // Alias for accessToken for backward compatibility
  expiresIn?: number;
  message?: string;
}


// **NEW: Token Verification Response Types**
export interface TokenVerificationRequest {
  token?: string; // Optional - can be extracted from header
}

export interface TokenVerificationResponse {
  success: boolean;
  message: string;
  code?: string;
  user?: {
    id: string;
    username: string;
    role: 'user' | 'admin' | 'contributor' | 'implementor';
    organization: {
      _id: string;
      name: string;
    } | string;
    groups: Array<{
      _id: string;
      name: string;
      type: 'visibility' | 'approval' | 'system';
    }> | string[];
    department?: string;
    lastLogin?: string;
    isActive: boolean;
  };
  tokenInfo?: {
    issuedAt: string;
    expiresAt: string;
    remainingTime: number;
  };
  expiredAt?: string;
  activeAt?: string;
}

// **NEW: Refresh Token Response**
export interface RefreshTokenResponse {
  accessToken: string;
  expiresIn: number; // 3600 seconds (1 hour)
}

// **NEW: Current User Response (from /me endpoint)**
export interface CurrentUserResponse {
  _id: string;
  id: string;
  username: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  role: 'user' | 'admin' | 'contributor' | 'implementor';
  organization: {
    _id: string;
    name: string;
  };
  groups: Array<{
    _id: string;
    name: string;
    type: 'visibility' | 'approval' | 'system';
  }>;
  department?: string;
  emailAccounts: EmailAccount[];
  createdAt: string;
  updatedAt: string;
  lastLoginAt?: string;
  deactivated: boolean;
  isActive: boolean;
  emailAccountCount: number;
  hasEmailAccounts: boolean;
}

// **NEW: Logout Response**
export interface LogoutResponse {
  message: string;
  timestamp: string;
}

// **NEW: Registration Response**
export interface RegistrationResponse {
  message: string;
  user: {
    _id: string;
    username: string;
    role: 'user' | 'admin' | 'contributor' | 'implementor';
    organization: string;
    organizationName: string;
    department?: string;
  };
}

// **NEW: Enhanced Auth Error Codes**
export type AuthErrorCode = 
  | 'TOKEN_MISSING'
  | 'TOKEN_EXPIRED' 
  | 'TOKEN_MALFORMED'
  | 'TOKEN_INVALID_STRUCTURE'
  | 'USER_NOT_FOUND'
  | 'ACCOUNT_DEACTIVATED'
  | 'TOKEN_NOT_ACTIVE'
  | 'TOKEN_VERIFICATION_FAILED'
  | 'INTERNAL_ERROR'
  | 'VALIDATION_ERROR'
  | 'INVALID_CREDENTIALS'
  | 'RATE_LIMIT_EXCEEDED'
  | 'ACCOUNT_LOCKED';

export interface AuthErrorResponse {
  success: false;
  message: string;
  code: AuthErrorCode;
  expiredAt?: string;
  activeAt?: string;
}

// **NEW: Password Management Types**
export interface PasswordChangeRequest {
  currentPassword: string;
  newPassword: string;
}

export interface PasswordResetRequest {
  token: string;
  newPassword: string;
}

export interface ForgotPasswordRequest {
  username: string;
  organization: string;
}

export interface UserRegistrationRequest {
  username: string;
  password: string;
  organization: string;
  role?: 'user' | 'admin';
  department?: string;
}

// **NEW: OAuth2 Types**
export interface OAuth2AuthorizeResponse {
  authorizationUrl: string;
  scopes: string[];
  state: string;
}

export interface EmailAccountsResponse {
  emailAccounts: EmailAccount[];
  user: {
    username: string;
    organization: string;
  };
  limits: {
    maxAccounts: number;
    currentCount: number;
    remaining: number;
  };
}

export interface EmailAccountStatsUpdateRequest {
  totalEmails: number;
  unreadEmails: number;
  syncStatus?: 'success' | 'error' | 'pending';
  errorMessage?: string;
}

export interface EmailAccountToggleRequest {
  isActive: boolean;
}

// ===== CREATE/UPDATE REQUEST TYPES =====

export interface UserCreateRequest {
  username: string;
  email?: string;
  phoneNumber?: string;
  firstName?: string;
  lastName?: string;
  password: string;
  role: UserRole;
  department?: string;
  organizationId?: string; // For implementor cross-org creation
}

export interface UserUpdateRequest {
  username?: string;
  email?: string;
  phoneNumber?: string;
  firstName?: string;
  lastName?: string;
  role?: UserRole;
  department?: string;
  isActive?: boolean;
  groups?: string[];
}

export interface OrganizationCreateRequest {
  // Basic organization info
  name: string;
  description?: string;
  
  // Resource limits (matching backend validation)
  maxUsers?: number;        // 1-1000
  maxProjects?: number;     // 1-200  
  maxGroups?: number;       // 1-100
  maxWorkflows?: number;    // 1-50
  
  // Email management settings (matching backend validation)
  maxEmailAccounts?: number;              // 1-100
  emailRetentionDays?: number;            // 1-2555
  maxEmailStorage?: number;               // min 100MB (100000000 bytes)
  maxCorrespondenceStorage?: number;      // min 100MB (100000000 bytes)
  maxTotalStorage?: number;               // Total storage limit
  emergencyPreservationEnabled?: boolean; // Emergency preservation flag
  
  // Storage and correspondence limits
  maxCorrespondences?: number;    // max 1000000
  maxStorage?: number;            // Legacy field for backward compatibility
  
  // Subscription and plan settings
  plan?: 'basic' | 'standard' | 'enterprise';
  isAIEnabled?: boolean;
  subscribedModules?: string[];
  
  // Organization status
  isActive?: boolean;
  
  // Admin user creation (required for new orgs)
  adminUser: {
    username: string;
    email: string;
    firstName: string;
    lastName: string;
    password?: string; // Optional, will be generated if not provided
  };
  
  // Email lifecycle and correspondence policies
  correspondencePolicy?: {
    autoClassificationEnabled?: boolean;
    requireManualReview?: boolean;
    preservationPriority?: 'high' | 'medium' | 'low';
    maxAttachmentSize?: number;
  };
}

export interface OrganizationUpdateRequest {
  // Basic info updates
  name?: string;
  description?: string;
  
  // Resource limit updates
  maxUsers?: number;
  maxProjects?: number;
  maxGroups?: number;
  maxWorkflows?: number;
  
  // Email settings updates
  maxEmailAccounts?: number;
  emailRetentionDays?: number;
  maxEmailStorage?: number;
  maxCorrespondenceStorage?: number;
  maxTotalStorage?: number;
  emergencyPreservationEnabled?: boolean;
  
  // Storage updates
  maxCorrespondences?: number;
  maxStorage?: number;
  
  // Plan and features
  plan?: 'basic' | 'standard' | 'enterprise';
  isAIEnabled?: boolean;
  subscribedModules?: string[];
  isActive?: boolean;
  
  // Correspondence policy updates
  correspondencePolicy?: {
    autoClassificationEnabled?: boolean;
    requireManualReview?: boolean;
    preservationPriority?: 'high' | 'medium' | 'low';
    maxAttachmentSize?: number;
  };
}

export interface GroupCreateRequest {
  name: string;
  type: 'visibility' | 'approval';
  description?: string;
  department?: string;
  members?: string[];
  organizationId?: string; // For implementor cross-org creation
}

export interface GroupUpdateRequest {
  name?: string;
  description?: string;
  department?: string;
  isActive?: boolean;
}

export interface AddMembersRequest {
  userIds: string[];
}

export interface RemoveMemberRequest {
  userId: string;
}

export interface ProjectCreateRequest {
  name: string;
  description?: string;
  priority?: Priority;
  isPublic?: boolean;
  visibilityGroups?: string[];
}

export interface ProjectUpdateRequest {
  name?: string;
  description?: string;
  priority?: Priority;
  isPublic?: boolean;
  visibilityGroups?: string[];
  isActive?: boolean;
}

export interface WorkflowCreateRequest {
  name: string;
  description?: string;
  stages: WorkflowStage[];
  priority?: Priority;
  isTemplate?: boolean;
  templateCategory?: 'standard' | 'expedited' | 'review-heavy' | 'simple' | 'custom';
  constraints?: WorkflowConstraints;
  organizationId?: string; // For implementors creating in specific org
  isActive?: boolean;
}

export interface WorkflowUpdateRequest {
  name?: string;
  description?: string;
  stages?: WorkflowStage[];
  priority?: Priority;
  isTemplate?: boolean;
  templateCategory?: 'standard' | 'expedited' | 'review-heavy' | 'simple' | 'custom';
  constraints?: WorkflowConstraints;
  isActive?: boolean;
}

export interface CorrespondenceCreateRequest {
  subject: string;
  body: string;
  projectId?: string;
  sourceEmailIds?: string[];
  sourceType: 'email' | 'manual' | 'imported';
  visibilityGroups?: string[];
  attachments?: string[];
  manual?: boolean;
}

export interface CorrespondenceUpdateRequest {
  subject?: string;
  body?: string;
  projectId?: string;
  visibilityGroups?: string[];
  manual?: boolean;
  direction?: 'inward' | 'outward' | 'internal';
  correspondenceStatus?: 'open' | 'partially_closed' | 'closed';
  documentType?: string;
}

export interface ResponseCreateRequest {
  sourceIds: string[]; // Array of correspondence IDs
  projectIds?: string[];
  visibilityGroups?: string[];
  workflowId: string;
  mailSubject: string;
  mailBody: string;
  responsePoints?: string[];
  scheduledSendAt?: string;
}

export interface ResponseUpdateRequest {
  mailSubject?: string;
  mailBody?: string;
  action: 'Edited' | 'Commented' | 'Approved' | 'Rejected' | 'Sent' | 'DraftUploaded';
  comment?: string;
  sourceIds?: string[];
}

export interface AttachmentUploadRequest {
  files: File[];
  emailId?: string;
  correspondenceId?: string;
  responseId?: string;
  attachmentType?: AttachmentType;
  isPublic?: boolean;
  description?: string;
  tags?: string[];
  /**
   * Called with 0-100 as the bytes go up. A large attachment can take minutes
   * and the screen used to sit still, so people assumed it had hung.
   * 100 means the upload finished; the server is then still storing and
   * reading the file, so callers should show "processing" rather than "done".
   */
  onProgress?: (percent: number) => void;
}

export interface AttachmentUpdateRequest {
  filename?: string;
  description?: string;
  tags?: string[];
  isPublic?: boolean;
  visibilityGroups?: string[];
}

// ===== EMAIL LIFECYCLE MANAGEMENT REQUESTS =====

export interface EmailPreservationRequest {
  emailIds: string[];
  preservationType: 'standard' | 'emergency';
  reason: string;
  retentionDays?: number;
}

export interface EmailRetentionPolicyRequest {
  emailRetentionDays: number;
  applyToExistingEmails?: boolean;
  reason: string;
}

export interface EmailRetentionPolicyUpdateRequest {
  emailRetentionDays: number;
  applyToExistingEmails?: boolean;
  reason: string;
}

export interface EmailCleanupRequest {
  targetDate?: string;
  preservationFilter?: 'all' | 'preserved_only' | 'temporary_only';
  dryRun?: boolean;
}

export interface EmailCleanupOperationRequest {
  organizationId: string;
  emailIds?: string[];
  cleanupType: 'scheduled' | 'manual' | 'emergency';
  reason: string;
  confirmDeletion: boolean;
}

export interface EmailBatchOperationRequest {
  emailIds: string[];
  operation: 'preserve' | 'delete' | 'update_retention' | 'emergency_preserve';
  preservationType?: 'standard' | 'emergency';
  retentionDays?: number;
  reason?: string;
}

export interface EmailAccountStatsUpdate {
  totalEmails?: number;
  storageUsed?: number;
  lastSyncAt?: string;
  syncStatus?: 'connected' | 'error' | 'pending';
}

// ===== BATCH OPERATION TYPES =====

export interface BatchOperationRequest {
  ids: string[];
  operation: string;
  options?: Record<string, any>;
}

export interface BatchDeleteRequest {
  ids: string[];
  confirmDeletion: boolean;
  reason?: string;
}

export interface BatchUpdateRequest {
  ids: string[];
  updates: Record<string, any>;
}

export interface BulkImportRequest {
  data: any[];
  validateOnly?: boolean;
  skipDuplicates?: boolean;
}

// ===== HEALTH CHECK AND SYSTEM TYPES =====

export interface HealthCheckResponse {
  status: 'healthy' | 'degraded' | 'unhealthy';
  timestamp: string;
  version?: string;
  uptime?: number;
  dependencies?: {
    database?: 'connected' | 'error';
    s3?: 'connected' | 'error';
    email?: 'connected' | 'error';
  };
  metrics?: {
    memoryUsage?: number;
    cpuUsage?: number;
    activeConnections?: number;
  };
}

export interface SystemStatsResponse {
  users: {
    total: number;
    active: number;
    byRole: Record<UserRole, number>;
  };
  organizations: {
    total: number;
    active: number;
  };
  storage: {
    total: number;
    used: number;
    available: number;
    usagePercentage: number;
  };
  processing: {
    emailsProcessed24h: number;
    correspondencesCreated24h: number;
    attachmentsUploaded24h: number;
  };
}

// ===== ANALYTICS AND REPORTING TYPES =====

export interface AnalyticsDateRange {
  startDate: string;
  endDate: string;
}

export interface EmailAnalyticsResponse {
  dateRange: AnalyticsDateRange;
  totalEmails: number;
  preservedEmails: number;
  autoDeleted: number;
  storageUsed: number;
  preservationRate: number;
  dailyBreakdown: Array<{
    date: string;
    received: number;
    preserved: number;
    deleted: number;
  }>;
}

export interface WorkflowAnalyticsResponse {
  dateRange: AnalyticsDateRange;
  totalResponses: number;
  completedResponses: number;
  averageCompletionTime: number;
  successRate: number;
  stageBreakdown: Array<{
    stageName: string;
    averageTime: number;
    successRate: number;
    bottleneck: boolean;
  }>;
}

export interface OrganizationAnalyticsResponse {
  organization: {
    id: string;
    name: string;
  };
  dateRange: AnalyticsDateRange;
  userActivity: {
    totalUsers: number;
    activeUsers: number;
    newUsers: number;
  };
  contentMetrics: {
    emailsProcessed: number;
    correspondencesCreated: number;
    responsesCompleted: number;
    attachmentsUploaded: number;
  };
  storageMetrics: StorageMetrics;
  performanceMetrics: {
    avgResponseTime: number;
    avgClassificationTime: number;
    avgWorkflowCompletionTime: number;
  };
}

// ===== EXCLUSION TYPES =====

export interface Exclusion {
  _id?: string;
  id?: string;
  name: string;
  type: 'sender' | 'subject' | 'domain' | 'content';
  pattern: string;
  isRegex?: boolean;
  isActive: boolean;
  organization: string;
  description?: string;
  
  // Usage statistics
  matchCount?: number;
  lastMatched?: string;
  
  // Audit fields
  createdBy: string;
  lastModifiedBy?: string;
  
  // Timestamps
  createdAt: string;
  updatedAt?: string;
  deletedAt?: string;
}

export interface ExclusionCreateRequest {
  name: string;
  type: 'sender' | 'subject' | 'domain' | 'content';
  pattern: string;
  isRegex?: boolean;
  description?: string;
  isActive?: boolean;
  organizationId?: string; // For implementor cross-org creation
}

export interface ExclusionUpdateRequest {
  name?: string;
  pattern?: string;
  isRegex?: boolean;
  description?: string;
  isActive?: boolean;
}

export interface ExclusionTestRequest {
  pattern: string;
  isRegex?: boolean;
  testData: string[];
}

export interface ExclusionQueryParams extends QueryParams {
  type?: 'sender' | 'subject' | 'domain' | 'content';
  isActive?: boolean;
  organizationId?: string;
}

// ===== STATIC DOCUMENT TYPES =====

export interface StaticDocument {
  _id?: string;
  id?: string;
  filename: string;
  contentType: string;
  size: number;
  s3Url?: string;
  filePath?: string;
  description?: string;
  category?: string;
  tags?: string[];
  organization: string;
  projectId?: string;

  // Access control
  isPublic?: boolean;
  visibilityGroups?: string[];

  // Tracking
  downloadCount?: number;
  lastAccessedAt?: string;
  processingStatus?: string;
  fileHash?: string;

  // Audit fields
  userId?: string;
  createdBy: string | { _id: string; name: string; email: string };
  updatedBy?: string;

  // Timestamps
  createdAt: string;
  updatedAt?: string;

  // Soft delete
  deletedAt?: string;
  deletedBy?: string;
}

export interface StaticDocumentCreateRequest {
  s3Url: string;
  filename: string;
  size: number;
  contentType: string;
  description?: string;
  category?: string;
  tags?: string[];
  isPublic?: boolean;
  visibilityGroups?: string[];
  projectId?: string;
}

export interface StaticDocumentUpdateRequest {
  filename?: string;
  description?: string;
  category?: string;
  tags?: string[];
  isPublic?: boolean;
  visibilityGroups?: string[];
}

export interface StaticDocumentQueryParams extends QueryParams {
  category?: string;
  isPublic?: boolean;
  tags?: string;
  contentType?: string;
  projectId?: string;
}

// ===== DASHBOARD AND SUMMARY TYPES =====

export interface DashboardQueryParams extends QueryParams {
  period?: 'day' | 'week' | 'month' | 'quarter' | 'year';
  includeDetails?: boolean;
  timezone?: string;
}

export interface DashboardMetrics {
  period: 'day' | 'week' | 'month' | 'quarter' | 'year';
  emails: {
    total: number;
    received: number;
    sent: number;
    preserved: number;
  };
  correspondences: {
    total: number;
    active: number;
    completed: number;
    overdue: number;
  };
  responses: {
    total: number;
    pending: number;
    approved: number;
    rejected: number;
  };
  storage: {
    used: number;
    limit: number;
    percentage: number;
  };
  timeRange: {
    start: string;
    end: string;
  };
}

export interface DashboardSummary {
  user: {
    id: string;
    username: string;
    role: UserRole;
    organizationName: string;
  };
  counts: {
    emails: number;
    correspondences: number;
    responses: number;
    projects: number;
    pendingApprovals?: number;
  };
  recentActivity: DashboardActivity[];
  upcomingTasks: DashboardTask[];
  storageUsage?: {
    used: number;
    limit: number;
    percentage: number;
  };
  notifications: {
    unread: number;
    urgent: number;
  };
}

export interface DashboardActivity {
  id: string;
  type: 'email' | 'correspondence' | 'response' | 'workflow';
  action: string;
  description: string;
  timestamp: string;
  entityId?: string;
  entityType?: string;
}

export interface DashboardTask {
  id: string;
  type: 'approval' | 'response' | 'review';
  title: string;
  description: string;
  priority: Priority;
  dueDate?: string;
  entityId: string;
  entityType: string;
}

// ===== ERROR HANDLING TYPES =====

export interface ValidationError {
  field: string;
  message: string;
  value?: any;
  code?: string;
}

export interface OperationResult<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  errors?: ValidationError[];
  message?: string;
  timestamp?: string;
}

// ===== API RESPONSE TYPES FOR EACH ENDPOINT =====

// ORGANIZATION API RESPONSES
export interface OrganizationCreateResponse {
  success: true;
  message: string;
  data: {
    organization: {
      id: string;
      _id?: string; // MongoDB ObjectId
      name: string;
      description?: string;
      
      // Resource limits
      maxUsers?: number;
      maxProjects?: number;
      maxGroups?: number;
      maxWorkflows?: number;
      
      // Email lifecycle settings
      maxEmailAccounts?: number;
      emailRetentionDays?: number;
      maxEmailStorage?: number;
      maxCorrespondenceStorage?: number;
      maxTotalStorage?: number;
      emergencyPreservationEnabled?: boolean;
      
      // Storage settings
      maxCorrespondences?: number;
      maxStorage?: number;
      
      // Plan and features
      plan?: 'basic' | 'standard' | 'enterprise';
      isAIEnabled?: boolean;
      subscribedModules?: string[];
      isActive?: boolean;
      
      // Timestamps
      createdAt: string;
      updatedAt?: string;
      
      // Metrics (initialized to zero for new orgs)
      numUsers?: number;
      numProjects?: number;
      numGroups?: number;
      numWorkflows?: number;
      numEmailAccounts?: number;
      numCorrespondences?: number;
      
      // Storage metrics
      storageMetrics?: {
        totalEmailStorage: number;
        totalCorrespondenceStorage: number;
        totalStorageUsage: number;
      };
      
      // Email metrics
      emailMetrics?: {
        totalEmails: number;
        preservedEmails: number;
        temporaryEmails: number;
        expiredEmails: number;
        emailPreservationRate: number;
        averageEmailRetentionDays: number;
      };
      
      // Correspondence metrics
      correspondenceMetrics?: {
        totalCorrespondences: number;
        autoClassifiedCorrespondences: number;
        manuallyReviewedCorrespondences: number;
        emailToCorrespondenceConversionRate: number;
      };
    };
    
    // Admin user info (created with the organization)
    adminUser: {
      id: string;
      _id?: string;
      username: string;
      email: string;
      firstName: string;
      lastName: string;
      role: 'admin';
      temporaryPassword?: string; // If password was generated
    };
    
    // Default groups created
    groups: {
      visibility: string | null;  // Group ID for "All Visibility"
      approval: string | null;    // Group ID for "All Approval" 
      total: number;              // Number of groups created
    };
    
    // Email lifecycle configuration applied
    emailLifecycleConfig: {
      defaultRetentionDays: number;
      autoPreservationEnabled: boolean;
      emergencyPreservationEnabled: boolean;
      correspondenceAutoClassification: boolean;
    };
    
    // Metadata about the creation process
    meta: {
      setupTime: number;              // Time taken to create org (ms)
      timestamp: string;              // ISO timestamp
      defaultGroupsCreated: number;   // Number of default groups
      version: string;                // API version
      emailLifecycleSupport: boolean; // Whether email lifecycle is enabled
    };
  };
  requestId: string;
}

export interface OrganizationListResponse extends PaginatedResponse<Organization> {
  analytics: {
    totalUsers: number;
    totalEmailStorage: number;
    totalCorrespondenceStorage: number;
    totalStorage: number;
    totalProjects: number;
    totalEmails: number;
    totalPreservedEmails: number;
    totalCorrespondences: number;
    activeOrgs: number;
    deactivatedOrgs: number;
    orgsWithRetentionPolicy: number;
    avgEmailPreservationRate: number;
    avgConversionRate: number;
    emailPreservationRatePercentage: number;
    correspondenceConversionRatePercentage: number;
    retentionPolicyAdoption: number;
  };
  filters: {
    search?: string;
    status?: string;
    sortBy?: string;
    sortOrder?: string;
    emailLifecycleCompliance?: string;
    storageType?: string;
  };
}

export interface OrganizationDetailResponse {
  success: true;
  data: {
    organization: Organization & {
      // Extended details for single org view
      groups?: Group[];
      users?: User[];
      projects?: Project[];
      workflows?: Workflow[];
      recentActivity?: Array<{
        type: string;
        description: string;
        timestamp: string;
        userId?: string;
        username?: string;
      }>;
      
      // Detailed metrics
      detailedMetrics?: {
        usersByRole: Record<UserRole, number>;
        projectsByPriority: Record<Priority, number>;
        emailsByStatus: {
          active: number;
          archived: number;
          deleted: number;
        };
        storageByType: {
          emails: number;
          correspondences: number;
          attachments: number;
          staticDocuments: number;
        };
      };
    };
  };
  requestId?: string;
}

export interface OrganizationUpdateResponse {
  organization: Organization;
  message: string;
  updatedFields: string[];
}

export interface OrganizationDeleteResponse {
  message: string;
  organizationId: string;
  deletedCount: {
    users: number;
    groups: number;
    projects: number;
    emails: number;
    correspondences: number;
  };
}

export interface OrganizationUsageResponse {
  organization: {
    id: string;
    name: string;
  };
  usage: {
    storage: StorageMetrics;
    users: {
      total: number;
      active: number;
      byRole: Record<UserRole, number>;
    };
    content: {
      emails: number;
      correspondences: number;
      responses: number;
      attachments: number;
    };
    activity: {
      emailsLast30Days: number;
      correspondencesLast30Days: number;
      responsesLast30Days: number;
      loginsLast30Days: number;
    };
  };
  quotas: {
    storageLimit: number;
    userLimit: number;
    emailRetentionLimit: number;
  };
  alerts: Array<{
    type: 'storage' | 'user_limit' | 'retention';
    severity: 'warning' | 'critical';
    message: string;
    threshold: number;
    current: number;
  }>;
}

// EMAIL LIFECYCLE API RESPONSES
export interface EmailLifecycleAnalyticsResponse {
  organization: {
    id: string;
    name: string;
  };
  dateRange: AnalyticsDateRange;
  metrics: {
    totalEmails: number;
    preservedEmails: number;
    pendingDeletion: number;
    autoDeletedEmails: number;
    emergencyPreservedEmails: number;
    correspondenceConversions: number;
    preservationRate: number;
    conversionRate: number;
  };
  trends: {
    daily: Array<{
      date: string;
      received: number;
      preserved: number;
      deleted: number;
      converted: number;
    }>;
    weekly: Array<{
      week: string;
      received: number;
      preserved: number;
      deleted: number;
      converted: number;
    }>;
  };
  storage: {
    emailStorage: number;
    correspondenceStorage: number;
    totalStorage: number;
    projectedGrowth: number;
  };
}

export interface EmailRetentionPolicyUpdateResponse {
  message: string;
  policy: EmailRetentionPolicy;
  impact: {
    affectedEmails: number;
    storageChange: number;
    estimatedDeletions: number;
  };
  timeline: string;
}

export interface EmailPreservationStatsResponse {
  organization: {
    id: string;
    name: string;
  };
  stats: {
    totalEmails: number;
    preservedEmails: number;
    temporaryEmails: number;
    emergencyPreserved: number;
    preservationRate: number;
  };
  breakdown: {
    byUser: Array<{
      userId: string;
      username: string;
      totalEmails: number;
      preservedEmails: number;
      preservationRate: number;
    }>;
    byProject: Array<{
      projectId: string;
      projectName: string;
      relatedEmails: number;
      preservedEmails: number;
    }>;
    byTimeframe: Array<{
      period: string;
      received: number;
      preserved: number;
      rate: number;
    }>;
  };
  policy: EmailRetentionPolicy;
}

export interface EmailCleanupOperationResponse {
  operation: {
    id: string;
    type: 'scheduled' | 'manual' | 'emergency';
    status: 'pending' | 'running' | 'completed' | 'error';
    startedAt: string;
    completedAt?: string;
    estimatedDuration?: number;
  };
  target: {
    organizationId: string;
    totalEmails: number;
    eligibleForDeletion: number;
    preservedEmails: number;
  };
  results?: {
    deletedEmails: number;
    preservedEmails: number;
    errors: number;
    storageFreed: number;
  };
  details: {
    reason: string;
    requestedBy: string;
    dryRun: boolean;
    preview?: Array<{
      emailId: string;
      subject: string;
      date: string;
      action: 'delete' | 'preserve' | 'skip';
    }>;
  };
}

export interface StorageOptimizationResponse {
  organization: {
    id: string;
    name: string;
  };
  current: StorageMetrics;
  recommendations: Array<{
    type: 'cleanup' | 'archive' | 'optimize';
    priority: Priority;
    title: string;
    description: string;
    potentialSavings: number;
    effort: 'low' | 'medium' | 'high';
    actions: string[];
  }>;
  projections: {
    nextMonth: number;
    nextQuarter: number;
    nextYear: number;
  };
  optimizations: {
    duplicateAttachments: number;
    unusedAttachments: number;
    largeAttachments: number;
    oldTemporaryFiles: number;
  };
}

// IMPLEMENTOR-SPECIFIC RESPONSES
export interface ImplementorDataResponse {
  implementor: {
    id: string;
    username: string;
    accessLevel: 'full' | 'restricted';
  };
  organizations: Array<{
    id: string;
    name: string;
    userCount: number;
    storageUsed: number;
    lastActivity: string;
    status: 'active' | 'deactivated';
    plan: string;
  }>;
  systemStats: {
    totalOrganizations: number;
    totalUsers: number;
    totalStorage: number;
    systemHealth: 'healthy' | 'degraded' | 'critical';
  };
  recentActivities: DashboardActivity[];
  alerts: Array<{
    organizationId: string;
    organizationName: string;
    type: 'storage' | 'user_limit' | 'system';
    severity: 'info' | 'warning' | 'critical';
    message: string;
    timestamp: string;
  }>;
}

// USER API RESPONSES
export interface UserListResponse extends PaginatedResponse<User> {
  analytics: {
    totalUsers: number;
    activeUsers: number;
    byRole: Record<UserRole, number>;
    recentLogins: number;
    newUsersThisMonth: number;
  };
  organization?: {
    id: string;
    name: string;
    userLimit?: number;
  };
}

export interface UserDetailResponse {
  user: User & {
    groups?: Group[];
    recentActivity?: DashboardActivity[];
    emailAccountStats?: Array<{
      email: string;
      provider: EmailProvider;
      emailCount: number;
      storageUsed: number;
      lastSync: string;
    }>;
  };
  permissions: {
    canEdit: boolean;
    canDelete: boolean;
    canResetPassword: boolean;
    canManageGroups: boolean;
  };
  stats: {
    emailsProcessed: number;
    correspondencesCreated: number;
    responsesCompleted: number;
    loginCount: number;
    lastActivity: string;
  };
}

export interface UserCreateResponse {
  user: User;
  message: string;
  temporaryPassword?: string;
  groups: {
    assigned: number;
    default: string[];
  };
}

export interface UserUpdateResponse {
  user: User;
  message: string;
  updatedFields: string[];
}

// GROUP API RESPONSES
export interface GroupListResponse extends PaginatedResponse<Group> {
  analytics: {
    totalGroups: number;
    visibilityGroups: number;
    approvalGroups: number;
    systemGroups: number;
    totalMembers: number;
    avgMembersPerGroup: number;
  };
  organization: {
    id: string;
    name: string;
  };
  metadata?: {
    userRole: string;
    accessLevel: 'full' | 'restricted' | 'member_only';
    organizationContext: 'specific' | 'cross_organization';
  };
}

export interface GroupDetailResponse {
  group: Group & {
    members?: User[];
    projects?: Project[];
    workflows?: Workflow[];
    recentActivity?: DashboardActivity[];
  };
  permissions: {
    canEdit: boolean;
    canDelete: boolean;
    canManageMembers: boolean;
  };
  usage: {
    projectCount: number;
    workflowCount: number;
    correspondenceAccess: number;
    memberActivity: Array<{
      userId: string;
      username: string;
      lastActivity: string;
      activityCount: number;
    }>;
  };
  dependencies: {
    projects: string[];
    workflows: string[];
    correspondences: number;
    canDelete: boolean;
    warnings: string[];
  };
}

export interface GroupCreateResponse {
  group: Group;
  message: string;
  defaultMembers?: string[];
}

export interface GroupUpdateResponse {
  group: Group;
  message: string;
  updatedFields: string[];
}

export interface GroupDeleteResponse {
  message: string;
  groupId: string;
  impact: {
    membersRemoved: number;
    projectsUpdated: number;
    workflowsUpdated: number;
  };
}

// WORKFLOW API RESPONSES
export interface WorkflowListResponse extends PaginatedResponse<Workflow> {
  filters?: {
    search?: string;
    sortBy?: string;
    sortOrder?: string;
    isTemplate?: boolean;
    priority?: string;
    isActive?: boolean;
    organizationId?: string;
  };
  metadata?: {
    organizationContext: 'specific' | 'cross_organization';
    userRole: string;
    accessLevel: 'full' | 'restricted' | 'error';
    totalActiveWorkflows: number;
    totalTemplates: number;
  };
}

export interface WorkflowDetailResponse {
  workflow: Workflow & {
    stages?: WorkflowStage[];
    recentUsage?: Array<{
      responseId: string;
      correspondenceSubject: string;
      createdAt: string;
      status: ResponseStatus;
    }>;
  };
  permissions: {
    canEdit: boolean;
    canDelete: boolean;
    canUse: boolean;
  };
  analytics?: {
    totalResponses: number;
    avgCompletionTime: number;
    successRate: number;
    stageBreakdown: Array<{
      stageName: string;
      avgTime: number;
      successRate: number;
      bottleneck: boolean;
    }>;
  };
  validation: WorkflowValidationResult;
}

export interface WorkflowCreateResponse {
  message: string;
  workflow: Workflow;
  validation: WorkflowValidationResult;
}

export interface WorkflowUpdateResponse {
  message: string;
  workflow: Workflow;
  updatedFields: string[];
  validation: WorkflowValidationResult;
}

export interface WorkflowDeleteResponse {
  message: string;
  workflowId: string;
  impact: {
    responsesAffected: number;
    templatesCreated: number;
  };
}

// WORKFLOW VALIDATION TYPES
export interface WorkflowValidationErrors {
  [key: string]: string[];
}

export interface StageValidation {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export interface WorkflowValidationResult {
  isValid: boolean;
  errors: WorkflowValidationErrors;
  warnings: WorkflowValidationErrors;
  stageValidations: StageValidation[];
}

// PROJECT API RESPONSES
export interface ProjectListResponse extends PaginatedResponse<Project> {
  analytics: {
    totalProjects: number;
    activeProjects: number;
    publicProjects: number;
    totalCorrespondences: number;
    totalResponses: number;
  };
  organization: {
    id: string;
    name: string;
  };
}

export interface ProjectDetailResponse {
  project: Project & {
    correspondences?: Correspondence[];
    visibilityGroups?: Group[];
    recentActivity?: DashboardActivity[];
  };
  permissions: {
    canEdit: boolean;
    canDelete: boolean;
    canCreateCorrespondence: boolean;
  };
  stats: {
    correspondenceCount: number;
    responseCount: number;
    attachmentCount: number;
    memberCount: number;
    recentActivityCount: number;
  };
}

export interface ProjectCreateResponse {
  project: Project;
  message: string;
  visibilityGroups: {
    assigned: number;
    names: string[];
  };
}

export interface ProjectUpdateResponse {
  project: Project;
  message: string;
  updatedFields: string[];
}

// CORRESPONDENCE API RESPONSES
export interface CorrespondenceListResponse extends PaginatedResponse<Correspondence> {
  analytics: {
    totalCorrespondences: number;
    manuallyCreated: number;
    fromEmails: number;
    withAttachments: number;
    withResponses: number;
    avgClassificationScore: number;
  };
  filters: {
    q?: string;
    projectId?: string;
    userId?: string;
    hasProject?: boolean;
    manual?: boolean;
    hasAttachments?: boolean;
    hasResponse?: boolean;
    dateFrom?: string;
    dateTo?: string;
    minScore?: number;
    maxScore?: number;
  };
}

export interface CorrespondenceDetailResponse {
  correspondence: Correspondence & {
    project?: Project;
    sourceEmails?: Email[];
    attachments?: Attachment[];
    responses?: Response[];
    visibilityGroups?: Group[];
  };
  permissions: {
    canEdit: boolean;
    canDelete: boolean;
    canCreateResponse: boolean;
    canViewAttachments: boolean;
  };
  analytics?: {
    viewCount: number;
    responseCount: number;
    avgResponseTime: number;
    lastActivity: string;
  };
}

export interface CorrespondenceCreateResponse {
  correspondence: Correspondence;
  message: string;
  classification?: {
    score: number;
    confidence: string;
    suggestedProject?: string;
  };
}

export interface CorrespondenceUpdateResponse {
  correspondence: Correspondence;
  message: string;
  updatedFields: string[];
}

// RESPONSE API RESPONSES
export interface ResponseListResponse extends PaginatedResponse<Response> {
  analytics: {
    totalResponses: number;
    draftResponses: number;
    inReviewResponses: number;
    sentResponses: number;
    avgCompletionTime: number;
  };
  workflows: Array<{
    id: string;
    name: string;
    responseCount: number;
  }>;
}

export interface ResponseDetailResponse {
  response: Response & {
    correspondence?: Correspondence;
    workflow?: Workflow;
    attachments?: Attachment[];
    comments?: ResponseComment[];
    history?: Array<{
      action: string;
      user: string;
      timestamp: string;
      comment?: string;
    }>;
  };
  permissions: {
    canEdit: boolean;
    canDelete: boolean;
    canApprove: boolean;
    canSend: boolean;
    canComment: boolean;
  };
  workflowStatus: {
    currentStage: string;
    nextStage?: string;
    canAdvance: boolean;
    requiredActions: string[];
  };
}

export interface ResponseCreateResponse {
  response: Response;
  message: string;
  workflow?: {
    id: string;
    name: string;
    firstStage: string;
  };
}

export interface ResponseUpdateResponse {
  response: Response;
  message: string;
  workflowAdvanced: boolean;
  nextStage?: string;
  notifications: string[];
}

// ATTACHMENT API RESPONSES
export interface AttachmentListResponse extends PaginatedResponse<Attachment> {
  analytics: {
    totalAttachments: number;
    totalSize: number;
    byType: Record<AttachmentType, number>;
    byProcessingStatus: Record<ProcessingStatus, number>;
    downloadCount: number;
  };
  storageBreakdown: {
    emailAttachments: number;
    correspondenceAttachments: number;
    responseAttachments: number;
    staticDocuments: number;
  };
}

export interface AttachmentDetailResponse {
  attachment: Attachment & {
    email?: Email;
    correspondence?: Correspondence;
    response?: Response;
    downloadHistory?: Array<{
      userId: string;
      username: string;
      downloadedAt: string;
      ipAddress?: string;
    }>;
  };
  permissions: {
    canView: boolean;
    canDownload: boolean;
    canEdit: boolean;
    canDelete: boolean;
  };
  processing: {
    contentExtracted: boolean;
    ocrApplied: boolean;
    virusScanned: boolean;
    thumbnailGenerated: boolean;
  };
}

export interface AttachmentUploadResult {
  filename: string;
  success: boolean;
  attachment?: Attachment;
  error?: string;
  existingAttachment?: {
    id: string;
    filename: string;
    createdAt: string;
  };
}

export interface AttachmentUploadResponse {
  attachments: AttachmentUploadResult[];
  summary: {
    total: number;
    successful: number;
    failed: number;
    totalSize: number;
  };
}

export interface AttachmentUpdateResponse {
  attachment: Attachment;
  message: string;
  updatedFields: string[];
}

// NOTIFICATION API RESPONSES
export interface NotificationListResponse extends PaginatedResponse<Notification> {
  summary: {
    total: number;
    unread: number;
    byType: Record<NotificationType, number>;
    byCategory: Record<NotificationCategory, number>;
    urgent: number;
  };
}

export interface NotificationDetailResponse {
  notification: Notification & {
    relatedEntity?: any;
  };
  permissions: {
    canMarkRead: boolean;
    canDelete: boolean;
  };
}

// EXCLUSION API RESPONSES
export interface ExclusionListResponse extends PaginatedResponse<Exclusion> {
  analytics: {
    totalExclusions: number;
    activeExclusions: number;
    totalMatches: number;
    byType: Record<string, number>;
    recentMatches: Array<{
      exclusionId: string;
      exclusionName: string;
      matchedContent: string;
      timestamp: string;
    }>;
  };
}

export interface ExclusionDetailResponse {
  exclusion: Exclusion & {
    recentMatches?: Array<{
      emailId: string;
      subject: string;
      matchedContent: string;
      timestamp: string;
    }>;
  };
  permissions: {
    canEdit: boolean;
    canDelete: boolean;
    canTest: boolean;
  };
  stats: {
    totalMatches: number;
    matchesLast30Days: number;
    avgMatchesPerDay: number;
    lastMatched?: string;
  };
}

export interface ExclusionCreateResponse {
  exclusion: Exclusion;
  message: string;
  validation: {
    patternValid: boolean;
    testResults?: Array<{
      input: string;
      matched: boolean;
    }>;
  };
}

export interface ExclusionTestResponse {
  results: Array<{
    input: string;
    matched: boolean;
    matchedPortion?: string;
  }>;
  pattern: string;
  isRegex: boolean;
  summary: {
    tested: number;
    matched: number;
    failed: number;
    matchRate: number;
  };
}

// STATIC DOCUMENT API RESPONSES
export interface StaticDocumentListResponse extends PaginatedResponse<StaticDocument> {
  analytics: {
    totalDocuments: number;
    publicDocuments: number;
    byCategory: Record<string, number>;
    totalSize: number;
  };
  categories: string[];
  tags: string[];
}

export interface StaticDocumentDetailResponse {
  document: StaticDocument & {
    attachments?: Attachment[];
    visibilityGroups?: Group[];
    versions?: Array<{
      version: number;
      createdAt: string;
      createdBy: string;
      changes: string;
    }>;
  };
  permissions: {
    canEdit: boolean;
    canDelete: boolean;
    canPublish: boolean;
    canManageVersions: boolean;
  };
  stats: {
    viewCount: number;
    downloadCount: number;
    shareCount: number;
    lastAccessed: string;
  };
}

// ===== TYPE GUARDS AND VALIDATORS =====

export class AuthTypeGuards {
  /**
   * Type guard for successful token verification
   */
  static isSuccessfulVerification(response: any): response is TokenVerificationResponse {
    return response && response.success === true && response.user && response.tokenInfo;
  }

  /**
   * Type guard for successful login
   */
  static isSuccessfulLogin(response: any): response is AuthResponse {
    return response && response.accessToken && response.user && response.user._id;
  }

  /**
   * Type guard for successful current user response
   */
  static isCurrentUserResponse(response: any): response is CurrentUserResponse {
    return response && response._id && response.username && response.role;
  }

  /**
   * Type guard for auth error responses
   */
  static isAuthError(response: any): response is AuthErrorResponse {
    return response && response.success === false && response.code;
  }

  /**
   * Type guard for expired token errors
   */
  static isTokenExpired(response: any): response is AuthErrorResponse & { code: 'TOKEN_EXPIRED' } {
    return this.isAuthError(response) && response.code === 'TOKEN_EXPIRED';
  }

  /**
   * Type guard for user not found errors
   */
  static isUserNotFound(response: any): response is AuthErrorResponse & { code: 'USER_NOT_FOUND' } {
    return this.isAuthError(response) && response.code === 'USER_NOT_FOUND';
  }

  /**
   * Type guard for account deactivated errors
   */
  static isAccountDeactivated(response: any): response is AuthErrorResponse & { code: 'ACCOUNT_DEACTIVATED' } {
    return this.isAuthError(response) && response.code === 'ACCOUNT_DEACTIVATED';
  }

  /**
   * Validates email account object
   */
  static isValidEmailAccount(account: any): account is EmailAccount {
    return account &&
           typeof account.email === 'string' &&
           typeof account.provider === 'string' &&
           typeof account.isActive === 'boolean' &&
           typeof account.totalEmails === 'number' &&
           typeof account.unreadEmails === 'number';
  }

  /**
   * Validates email accounts response
   */
  static isValidEmailAccountsResponse(response: any): response is EmailAccountsResponse {
    return response &&
           Array.isArray(response.emailAccounts) &&
           response.user &&
           response.limits &&
           typeof response.limits.maxAccounts === 'number';
  }
}

export class UserValidators {
  /**
   * Validates if organization is string format
   */
  static isValidOrganizationString(org: any): org is string {
    return typeof org === 'string' && org.length > 0;
  }

  /**
   * Validates if organization is object format
   */
  static isValidOrganizationObject(org: any): org is Organization {
    return org && typeof org === 'object' && (org._id || org.id) && org.name;
  }

  /**
   * Type guard for valid user object
   */
  static isValidUser(user: any): user is User {
    if (!user || typeof user !== 'object') return false;
    if (!user._id || typeof user._id !== 'string') return false;
    if (!user.username || typeof user.username !== 'string') return false;
    if (!user.role || !['user', 'admin', 'contributor', 'implementor'].includes(user.role)) return false;
    if (typeof user.createdAt !== 'string') return false;
    
    const hasValidOrg = this.isValidOrganizationString(user.organization) || 
                       this.isValidOrganizationObject(user.organization);
    if (!hasValidOrg) return false;
    
    if (user.groups && !Array.isArray(user.groups)) return false;
    
    return true;
  }

  /**
   * Type guard for checking if user has implementor role
   */
  static isImplementor(user: User): boolean {
    return user.role === 'implementor';
  }

  /**
   * Type guard for checking if user has admin role
   */
  static isAdmin(user: User): boolean {
    return user.role === 'admin';
  }

  /**
   * Type guard for checking if user can access multiple organizations
   */
  static canAccessMultipleOrgs(user: User): boolean {
    return user.role === 'implementor';
  }
}

export class ApiResponseValidators {
  /**
   * Type guard for successful API responses
   */
  static isSuccessResponse<T>(response: any): response is StandardResponse<T> {
    return response && (response.success === true || response.success === undefined) && !response.error;
  }

  /**
   * Type guard for error responses
   */
  static isErrorResponse(response: any): response is ApiErrorResponse {
    return response && (response.success === false || response.error);
  }

  /**
   * Type guard for paginated responses
   */
  static isPaginatedResponse<T>(response: any): response is PaginatedResponse<T> {
    return response && Array.isArray(response.data) && response.pagination;
  }
}

// ===== UTILITY TYPES AND HELPERS =====

export interface ApiMetadata {
  requestId?: string;
  timestamp?: string;
  version?: string;
  processingTime?: number;
  cacheHit?: boolean;
}

export interface SortOptions {
  field: string;
  direction: 'asc' | 'desc';
}

export interface FilterOptions {
  [key: string]: any;
}

export interface PaginationOptions {
  page?: number;
  limit?: number;
  offset?: number;
}

export interface SearchOptions {
  query?: string;
  fields?: string[];
  fuzzy?: boolean;
  exact?: boolean;
}

export interface ApiRequest<T = any> {
  data?: T;
  params?: QueryParams;
  headers?: Record<string, string>;
  timeout?: number;
}

export interface ApiClient {
  instance: any;
  get<T = any>(url: string, config?: ApiRequest): Promise<T>;
  post<T = any>(url: string, data?: any, config?: ApiRequest): Promise<T>;
  put<T = any>(url: string, data?: any, config?: ApiRequest): Promise<T>;
  delete<T = any>(url: string, config?: ApiRequest): Promise<T>;
  generatePublicRequestId(): string;
}


// ===== CACHE MANAGEMENT TYPES =====

export interface CacheEntry {
  data: any;
  timestamp: number;
  ttl: number;
  key: string;
}

export interface CacheOptions {
  ttl?: number;
  key?: string;
  tags?: string[];
  invalidateOnError?: boolean;
}

export interface CacheManager {
  get<T>(key: string): T | null;
  set<T>(key: string, data: T, options?: CacheOptions): void;
  delete(key: string): void;
  clear(pattern?: string): void;
  has(key: string): boolean;
}

// ===== FILE HANDLING TYPES =====

export interface FileUploadOptions {
  maxSize?: number;
  allowedTypes?: string[];
  multiple?: boolean;
  compress?: boolean;
  generateThumbnail?: boolean;
}

export interface FileProcessingResult {
  success: boolean;
  file?: UploadedFile;
  error?: string;
  processingTime?: number;
  extractedContent?: string;
  thumbnail?: string;
}

export interface BulkFileUploadResult {
  successful: UploadedFile[];
  failed: Array<{
    filename: string;
    error: string;
  }>;
  summary: {
    total: number;
    successful: number;
    failed: number;
    totalSize: number;
  };
}

// ===== WORKFLOW EXECUTION TYPES =====

export interface WorkflowExecution {
  id: string;
  workflowId: string;
  responseId: string;
  status: 'running' | 'completed' | 'failed' | 'cancelled';
  currentStage: number;
  
  stages: Array<{
    stageId: string;
    stageName: string;
    status: 'pending' | 'active' | 'completed' | 'skipped' | 'failed';
    assignedTo?: string;
    startedAt?: string;
    completedAt?: string;
    timeSpent?: number;
    comments?: string;
  }>;
  
  startedAt: string;
  completedAt?: string;
  totalTime?: number;
  
  metadata: {
    priority: Priority;
    createdBy: string;
    organizationId: string;
  };
}

export interface WorkflowStageAction {
  action: 'approve' | 'reject' | 'edit' | 'comment' | 'forward';
  comment?: string;
  assignTo?: string;
  scheduledFor?: string;
}

export interface WorkflowTransition {
  fromStage: number;
  toStage: number;
  condition?: string;
  automatic: boolean;
  delay?: number;
}

// ===== AUDIT AND LOGGING TYPES =====

export interface AuditLog {
  id: string;
  userId: string;
  organizationId: string;
  action: string;
  resource: string;
  resourceId?: string;
  
  details: {
    method?: string;
    endpoint?: string;
    userAgent?: string;
    ipAddress?: string;
    changes?: Record<string, { old: any; new: any }>;
  };
  
  result: 'success' | 'failure' | 'error';
  errorMessage?: string;
  
  timestamp: string;
  requestId?: string;
}

export interface ActivityLog {
  id: string;
  userId: string;
  organizationId: string;
  type: 'login' | 'logout' | 'create' | 'update' | 'delete' | 'view' | 'download';
  description: string;
  
  entityType?: string;
  entityId?: string;
  entityName?: string;
  
  metadata?: Record<string, any>;
  timestamp: string;
}

// ===== SEARCH AND INDEXING TYPES =====

export interface SearchQuery {
  query: string;
  filters?: FilterOptions;
  sort?: SortOptions[];
  pagination?: PaginationOptions;
  fields?: string[];
  highlight?: boolean;
  fuzzy?: boolean;
}

export interface SearchResult<T = any> {
  hits: Array<{
    document: T;
    score: number;
    highlights?: Record<string, string[]>;
  }>;
  total: number;
  maxScore: number;
  aggregations?: Record<string, any>;
  facets?: Record<string, Array<{
    value: string;
    count: number;
  }>>;
  took: number;
}

export interface IndexDocument {
  id: string;
  type: 'email' | 'correspondence' | 'response' | 'attachment' | 'user' | 'project';
  organizationId: string;
  content: string;
  metadata: Record<string, any>;
  timestamp: string;
}

// ===== REAL-TIME UPDATES TYPES =====

export interface WebSocketMessage {
  type: string;
  payload: any;
  timestamp: string;
  userId?: string;
  organizationId?: string;
}

export interface RealtimeUpdate {
  type: 'create' | 'update' | 'delete';
  resource: string;
  resourceId: string;
  data?: any;
  userId: string;
  organizationId: string;
  timestamp: string;
}

export interface NotificationUpdate extends RealtimeUpdate {
  notification: Notification;
  targetUsers: string[];
}

export interface WorkflowUpdate extends RealtimeUpdate {
  responseId: string;
  workflowId: string;
  stageChange: {
    from: string;
    to: string;
    assignedTo: string;
  };
}

// ===== INTEGRATION TYPES =====

export interface EmailProviderIntegration {
  type: 'gmail' | 'outlook' | 'imap' | 'exchange';
  config: {
    host?: string;
    port?: number;
    secure?: boolean;
    auth: {
      user: string;
      pass?: string;
      accessToken?: string;
      refreshToken?: string;
    };
  };
  status: 'connected' | 'error' | 'pending' | 'disconnected';
  lastSync?: string;
  syncFrequency?: number;
}

export interface StorageProviderIntegration {
  type: 's3' | 'azure' | 'gcp' | 'local';
  config: {
    bucket?: string;
    region?: string;
    accessKey?: string;
    secretKey?: string;
    endpoint?: string;
  };
  status: 'connected' | 'error' | 'pending';
}

export interface AIProviderIntegration {
  type: 'openai' | 'azure-openai' | 'anthropic' | 'local';
  config: {
    apiKey?: string;
    endpoint?: string;
    model?: string;
    temperature?: number;
  };
  status: 'active' | 'inactive' | 'error';
  usage?: {
    requestsToday: number;
    tokensUsed: number;
    cost: number;
  };
}

// ===== BACKUP AND RECOVERY TYPES =====

export interface BackupInfo {
  id: string;
  organizationId: string;
  type: 'full' | 'incremental' | 'differential';
  status: 'pending' | 'running' | 'completed' | 'failed';
  
  size: number;
  location: string;
  encryption: boolean;
  
  startedAt: string;
  completedAt?: string;
  duration?: number;
  
  includes: {
    users: boolean;
    emails: boolean;
    correspondences: boolean;
    attachments: boolean;
    configurations: boolean;
  };
  
  metadata: {
    version: string;
    createdBy: string;
    retentionDays: number;
  };
}

export interface RestoreOptions {
  backupId: string;
  restoreType: 'full' | 'selective';
  targetOrganization?: string;
  
  includes?: {
    users?: boolean;
    emails?: boolean;
    correspondences?: boolean;
    attachments?: boolean;
    configurations?: boolean;
  };
  
  conflictResolution: 'overwrite' | 'skip' | 'merge';
  validateBeforeRestore: boolean;
}

// ===== SYSTEM CONFIGURATION TYPES =====

export interface SystemConfig {
  general: {
    siteName: string;
    siteUrl: string;
    timezone: string;
    language: string;
    maintenanceMode: boolean;
  };
  
  email: {
    smtpHost: string;
    smtpPort: number;
    smtpSecure: boolean;
    smtpAuth: {
      user: string;
      pass: string;
    };
    fromAddress: string;
    fromName: string;
  };
  
  storage: {
    provider: string;
    maxFileSize: number;
    allowedFileTypes: string[];
    retentionDays: number;
    compressionEnabled: boolean;
  };
  
  security: {
    sessionTimeout: number;
    passwordPolicy: {
      minLength: number;
      requireUppercase: boolean;
      requireLowercase: boolean;
      requireNumbers: boolean;
      requireSymbols: boolean;
    };
    mfaEnabled: boolean;
    ipWhitelist: string[];
  };
  
  ai: {
    enabled: boolean;
    provider: string;
    classificationThreshold: number;
    autoClassificationEnabled: boolean;
  };
}

// ===== PERFORMANCE MONITORING TYPES =====

export interface PerformanceMetrics {
  responseTime: {
    avg: number;
    p50: number;
    p95: number;
    p99: number;
  };
  
  throughput: {
    requestsPerSecond: number;
    requestsPerMinute: number;
    requestsPerHour: number;
  };
  
  errorRate: {
    percentage: number;
    count: number;
    by4xx: number;
    by5xx: number;
  };
  
  systemLoad: {
    cpuUsage: number;
    memoryUsage: number;
    diskUsage: number;
    networkIO: number;
  };
  
  database: {
    connections: number;
    activeQueries: number;
    avgQueryTime: number;
    slowQueries: number;
  };
  
  timestamp: string;
  interval: number;
}

export interface HealthStatus {
  overall: 'healthy' | 'degraded' | 'unhealthy';
  components: {
    api: 'up' | 'down' | 'degraded';
    database: 'up' | 'down' | 'degraded';
    storage: 'up' | 'down' | 'degraded';
    email: 'up' | 'down' | 'degraded';
    ai: 'up' | 'down' | 'degraded';
  };
  uptime: number;
  version: string;
  timestamp: string;
}

// ===== TYPE UTILITY FUNCTIONS =====

/**
 * Helper function to extract ID from object or string
 */
export function extractId(item: string | { _id?: string; id?: string }): string | null {
  if (typeof item === 'string') return item;
  if (item && typeof item === 'object') return item._id || item.id || null;
  return null;
}

/**
 * Helper function to normalize user object
 */
export function normalizeUser(user: User): NormalizedUser {
  const orgId = extractId(user.organization);
  const orgName = typeof user.organization === 'object' ? user.organization.name : user.organizationName || 'Unknown';
  
  // Ensure we have an ID
  const userId = user._id || user.id;
  if (!userId) {
    throw new Error('User object must have either _id or id property');
  }
  
  return {
    _id: userId,
    id: userId,
    username: user.username,
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    role: user.role,
    organization: {
      _id: orgId || '',
      name: orgName
    },
    groups: Array.isArray(user.groups) ? user.groups.map(group => {
      if (typeof group === 'string') {
        return { _id: group, name: 'Unknown', type: 'visibility' as const };
      }
      return {
        _id: group._id || group.id || '',
        name: group.name || 'Unknown',
        type: group.type || 'visibility' as const
      };
    }) : [],
    department: user.department,
    emailAccounts: user.emailAccounts || [],
    createdAt: user.createdAt,
    updatedAt: user.updatedAt || user.createdAt,
    lastLoginAt: user.lastLoginAt || user.lastLogin,
    deactivated: user.deactivated || false,
    deletedAt: user.deletedAt,
    loginAttempts: user.loginAttempts,
    lockUntil: user.lockUntil,
    isActive: user.isActive ?? !user.deactivated,
    emailAccountCount: user.emailAccounts?.length || 0,
    groupCount: Array.isArray(user.groups) ? user.groups.length : 0
  };
}

/**
 * Helper function to normalize organization object
 */
export function normalizeOrganization(org: any): Organization {
  // Handle both MongoDB _id and standard id
  const id = org.id || org._id;
  if (!id) {
    console.warn('Organization missing ID field:', org);
  }

  return {
    ...org,
    // Ensure both id formats are available
    _id: org._id || org.id,
    id: id || '', // Provide empty string as fallback to satisfy required field
    
    // Map backend field names to frontend aliases
    userCount: org.userCount || org.numUsers || 0,
    projectCount: org.projectCount || org.numProjects || 0,
    emailCount: org.emailCount || org.numEmails || (org.emailMetrics?.totalEmails) || 0,
    correspondenceCount: org.correspondenceCount || org.numCorrespondences || (org.correspondenceMetrics?.totalCorrespondences) || 0,
    
    // Preserve original backend field names
    numUsers: org.numUsers || org.userCount || 0,
    numProjects: org.numProjects || org.projectCount || 0,
    numEmails: org.numEmails || org.emailCount || (org.emailMetrics?.totalEmails) || 0,
    numCorrespondences: org.numCorrespondences || org.correspondenceCount || (org.correspondenceMetrics?.totalCorrespondences) || 0,
    
    // Status normalization
    isActive: org.isActive ?? (org.deactivated != null ? !org.deactivated : true),
    deactivated: org.deactivated ?? (org.isActive != null ? !org.isActive : false),
    
    // Ensure metrics exist
    storageMetrics: org.storageMetrics || {
      totalEmailStorage: 0,
      totalCorrespondenceStorage: 0,
      totalStorageUsage: 0
    },
    
    emailMetrics: org.emailMetrics || {
      totalEmails: 0,
      preservedEmails: 0,
      temporaryEmails: 0,
      expiredEmails: 0,
      emailPreservationRate: 0,
      averageEmailRetentionDays: 0
    },
    
    correspondenceMetrics: org.correspondenceMetrics || {
      totalCorrespondences: 0,
      manualCorrespondences: 0,
      autoClassifiedCorrespondences: 0,
      emailToCorrespondenceConversionRate: 0
    }
  };
}

/**
 * Helper function to check if user can access organization
 */
export function canAccessOrganization(user: User, organizationId: string): boolean {
  if (user.role === 'implementor') return true;
  
  const userOrgId = extractId(user.organization);
  return userOrgId === organizationId;
}

/**
 * Helper function to format file size
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Helper function to format date relative to now
 */
export function formatRelativeTime(date: string | Date): string {
  const now = new Date();
  const then = new Date(date);
  const diffInSeconds = Math.floor((now.getTime() - then.getTime()) / 1000);
  
  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`;
  
  const day = String(then.getDate()).padStart(2, '0');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = months[then.getMonth()];
  return `${day}-${month}-${then.getFullYear()}`;
}

/**
 * Helper function to validate email format
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Helper function to generate cache key
 */
export function generateCacheKey(prefix: string, params: Record<string, any>): string {
  const sortedParams = Object.keys(params)
    .sort()
    .map(key => `${key}:${JSON.stringify(params[key])}`)
    .join('|');
  
  return `${prefix}:${btoa(sortedParams)}`;
}

/**
 * Helper function to deep merge objects
 */
export function deepMerge<T extends Record<string, any>>(target: T, source: Partial<T>): T {
  const result = { ...target };
  
  for (const key in source) {
    if (source[key] !== undefined) {
      if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key]) && 
          target[key] && typeof target[key] === 'object' && !Array.isArray(target[key])) {
        (result as any)[key] = deepMerge(target[key] as Record<string, any>, source[key] as Record<string, any>);
      } else {
        (result as any)[key] = source[key];
      }
    }
  }
  
  return result;
}

/**
 * Helper function to debounce function calls
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  
  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Helper function to throttle function calls
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;
  
  return function executedFunction(...args: Parameters<T>) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}